import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import { FilterBar, FilterEnd, FilterSearch, FilterSelect } from '../components/FilterBar';
import PageLayout from '../components/ui/PageLayout';
import { Field } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import Tooltip from '../components/ui/Tooltip';
import Alert from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import ListRow, { ListStack } from '../components/ui/ListRow';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { formatTimestamp, parseTimestamp } from '../utils/date-utils';
import { createPlan, errorMessage, listPlans } from './api';
import type { PlanRole, PlanSummary } from './types';

const ROLE_LABEL: Record<PlanRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const personName = (p: { firstname: string; lastname: string }) =>
  `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Unknown';

/** "just now", "12 min ago", "2 h ago", "yesterday", "4 days ago", then a date. */
const relativeLabel = (iso: string): string => {
  const then = parseTimestamp(iso);
  if (!then || !then.isValid()) return '';
  const now = dayjs();
  const mins = now.diff(then, 'minute');
  if (mins < 1) return 'just now';
  const days = now.startOf('day').diff(then.startOf('day'), 'day');
  if (days <= 0) return mins < 60 ? `${mins} min ago` : `${now.diff(then, 'hour')} h ago`;
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.format(then.year() === now.year() ? 'MMM D' : 'MMM D, YYYY');
};

// ── Filters, kept in the URL so a bookmark or a back button keeps them ──

type Mine = 'owned' | 'shared' | 'all';
type SortKey = 'modified' | 'created';

const MINE_OPTIONS = [
  { key: 'owned', label: 'Owned by me' },
  { key: 'shared', label: 'Shared with me' },
  { key: 'all', label: 'All' },
];

interface Filters {
  q: string;
  mine: Mine;
  sort: SortKey;
}

const readFilters = (search: string): Filters => {
  const p = new URLSearchParams(search);
  const mine = p.get('mine');
  return {
    q: p.get('q') ?? '',
    mine: mine === 'owned' || mine === 'shared' ? mine : 'all',
    sort: p.get('sort') === 'created' ? 'created' : 'modified',
  };
};

/** Defaults stay out of the URL, so a plain /node-plans is the unfiltered list. */
const writeFilters = (f: Filters): string => {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.mine !== 'all') p.set('mine', f.mine);
  if (f.sort !== 'modified') p.set('sort', f.sort);
  const out = p.toString();
  return out ? `?${out}` : '';
};

const stamp = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
};

const applyFilters = (plans: PlanSummary[], f: Filters): PlanSummary[] => {
  const q = f.q.trim().toLowerCase();
  const field = f.sort === 'created' ? 'created_at' : 'updated_at';
  return plans
    .filter((p) => (f.mine === 'owned' ? p.role === 'owner' : f.mine === 'shared' ? p.role !== 'owner' : true))
    .filter((p) => !q || p.title.toLowerCase().includes(q) || personName(p.owner).toLowerCase().includes(q))
    .sort((a, b) => stamp(b[field]) - stamp(a[field]));
};

const help = (
  <div className="space-y-3 text-sm text-on-surface-variant">
    <p>A plan is a shared outline where every topic is a node, numbered like a document.</p>
    <p>
      The owner and plan admins place topics and set their order; members propose topics, which wait in
      Proposed until someone accepts them.
    </p>
    <p>
      A topic can have one authority: a person on the plan who may change that topic, and draw arrows from it, as
      an admin would.
    </p>
    <p>Plans are private: only the people on a plan can open it, unless the owner opens it to everyone signed in.</p>
  </div>
);

interface NewPlanFieldProps {
  onCancel: () => void;
  onCreated: (plan: PlanSummary) => void;
}

const NewPlanField: React.FC<NewPlanFieldProps> = ({ onCancel, onCreated }) => {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    const clean = title.trim();
    if (!clean || busy) return;
    setBusy(true);
    try {
      const { plan } = await createPlan({ title: clean });
      onCreated(plan);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not create the plan.'));
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl bg-surface-container-low p-3">
      <Field
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        maxLength={200}
        disabled={busy}
        aria-label="Plan name"
        placeholder="Name the plan"
        className="min-h-[40px] flex-1 basis-60"
      />
      <PrimaryAction onClick={submit} disabled={!title.trim() || busy}>
        {busy ? 'Creating' : 'Create'}
      </PrimaryAction>
      <SecondaryAction variant="text" onClick={onCancel}>
        Cancel
      </SecondaryAction>
    </div>
  );
};

const STATE_CHIP = 'rounded-full bg-surface-container-highest px-2 py-0.5 text-[11px] font-medium text-on-surface-variant';

const PlanRow: React.FC<{ plan: PlanSummary; sort: SortKey }> = ({ plan, sort }) => {
  const iso = sort === 'created' ? plan.created_at : plan.updated_at;
  return (
    <ListRow to={`/node-plans/${plan.id}`}>
      <span className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
        <span className="block min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <Tooltip label={plan.title} truncated>
              <span className="truncate text-base font-semibold text-on-surface">{plan.title}</span>
            </Tooltip>
            {plan.locked_at && <span className={STATE_CHIP}>Locked</span>}
            {plan.archived_at && <span className={STATE_CHIP}>Archived</span>}
          </span>
          <Tooltip label={`${personName(plan.owner)}${plan.description ? `, ${plan.description}` : ''}`} truncated>
            <span className="mt-0.5 block truncate text-xs text-on-surface-variant">
              {personName(plan.owner)}
              {plan.description ? `, ${plan.description}` : ''}
            </span>
          </Tooltip>
        </span>
        <span className="flex flex-shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-on-surface-variant">
          {plan.role !== 'owner' && <span>{ROLE_LABEL[plan.role] ?? plan.role}</span>}
          <span className="tabular-nums">
            {plan.node_count} {plan.node_count === 1 ? 'topic' : 'topics'}
          </span>
          <Tooltip label={formatTimestamp(iso, 'MMM D, YYYY h:mm A')}>
            <span className="whitespace-nowrap">
              {sort === 'created' ? 'Created' : 'Modified'} {relativeLabel(iso)}
            </span>
          </Tooltip>
        </span>
      </span>
    </ListRow>
  );
};

const NodePlansPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const filters = readFilters(location.search);
  const setFilters = (patch: Partial<Filters>) =>
    history.replace({ pathname: location.pathname, search: writeFilters({ ...filters, ...patch }) });
  const [plans, setPlans] = useState<PlanSummary[] | null>(null);
  const [failed, setFailed] = useState<null | 'signed_out' | 'failed'>(null);
  const logout = useUserStore((s: any) => s.logout);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const { items } = await listPlans();
      setPlans(items);
    } catch (e) {
      setFailed((e as { status?: number } | null)?.status === 401 ? 'signed_out' : 'failed');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreated = (plan: PlanSummary) => history.push(`/node-plans/${plan.id}`);

  const shown = useMemo(
    () => (plans ? applyFilters(plans, filters) : []),
    // `filters` is rebuilt from the URL each render; its fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plans, filters.q, filters.mine, filters.sort],
  );
  const bodyState = failed ? 'failed' : plans === null ? 'loading' : plans.length === 0 ? 'empty' : shown.length === 0 ? 'nomatch' : 'list';
  const filtered = filters.q.trim() !== '' || filters.mine !== 'all';

  return (
    <PageLayout kind="workspace" className="pt-4 lg:pt-6">
      <PageHeader
        title="Node Plans"
        lede="Plan together, one topic at a time. Everyone's notes stay their own."
        help={help}
        primary={creating ? undefined : <PrimaryAction onClick={() => setCreating(true)}>New plan</PrimaryAction>}
      />

      <Reveal show={creating}>
        <div className="-m-1 p-1">
          <NewPlanField onCancel={() => setCreating(false)} onCreated={onCreated} />
        </div>
      </Reveal>

      {plans && plans.length > 0 && (
        <FilterBar label="Filter plans" className="mt-5">
          <FilterSearch
            value={filters.q}
            onChange={(q) => setFilters({ q })}
            label="Search plans by title or owner"
            placeholder="Search plans"
          />
          <SegTrack
            label="Which plans to show"
            size="sm"
            wrap
            options={MINE_OPTIONS}
            value={filters.mine}
            onChange={(mine) => setFilters({ mine: mine as Mine })}
          />
          <FilterSelect label="Sort by" value={filters.sort} onChange={(sort) => setFilters({ sort: sort as SortKey })}>
            <option value="modified">Modified</option>
            <option value="created">Created</option>
          </FilterSelect>
          <FilterEnd>
            <span role="status">
              {filtered ? `${shown.length} of ${plans.length}` : plans.length} {plans.length === 1 ? 'plan' : 'plans'}
            </span>
          </FilterEnd>
        </FilterBar>
      )}

      <FadeIn key={bodyState} className="mt-6">
        {failed === 'signed_out' ? (
          <Alert
            tone="warning"
            action={<Button variant="tonal" onClick={logout}>Sign in again</Button>}
          >
            Your session has ended.
          </Alert>
        ) : failed ? (
          <Alert onRetry={load}>Could not load your plans.</Alert>
        ) : plans === null ? (
          <div aria-busy="true">
            <SkeletonGroup label="Loading plans" className="grid gap-px">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[60px] rounded-panel" />
              ))}
            </SkeletonGroup>
          </div>
        ) : plans.length === 0 ? (
          !creating && (
            <EmptyState
              title="No plans yet"
              titleAs="h2"
              action={<Button variant="tonal" onClick={() => setCreating(true)}>New plan</Button>}
            >
              A plan is an outline your team builds together: one topic per node, numbered like a document.
              Start one, then add topics as they come up.
            </EmptyState>
          )
        ) : shown.length === 0 ? (
          <EmptyState
            title="No plans match"
            titleAs="h2"
            action={<Button variant="tonal" onClick={() => setFilters({ q: '', mine: 'all' })}>Clear filters</Button>}
          >
            {filters.q.trim()
              ? `Nothing ${filters.mine === 'owned' ? 'you own' : filters.mine === 'shared' ? 'shared with you' : 'here'} has "${filters.q.trim()}" in its title or owner.`
              : filters.mine === 'owned'
                ? 'You do not own any plans yet.'
                : 'No one has shared a plan with you yet.'}
          </EmptyState>
        ) : (
          <ListStack
            items={shown}
            getKey={(p) => p.id}
            renderRow={(p) => <PlanRow plan={p} sort={filters.sort} />}
            label="Plans"
          />
        )}
      </FadeIn>
    </PageLayout>
  );
};

export default NodePlansPage;
